import { AfterViewInit, Component, ElementRef, forwardRef, Input, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DataManager } from '@syncfusion/ej2-data';
import { Observable, of } from 'rxjs';
import { AutoCompleteComponent, AutoCompleteModule } from '@syncfusion/ej2-angular-dropdowns';
import { IDynamicMultiSelectItem } from './dynamic-multi-select-item';
import { TranslatePipe } from '@ngx-translate/core';
import { IDynamicAutocompleteItem } from './dynamic-autocomplete-item';
import { DynamicAutocompleteCustomAdaptor } from './dynamic-autocomplete-custom-adaptor';
import { DynamicAutocompleteInitialItemAdaptor } from './dynamic-autocomplete-initial-item-adaptor';

@Component({
    selector: 'app-dynamic-autocomplete',
    templateUrl: './dynamic-autocomplete.component.html',
    styleUrls: ['./dynamic-autocomplete.component.scss'],
    imports: [AutoCompleteModule, TranslatePipe],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [
      {
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => DynamicAutocompleteComponent),
        multi: true,
      },
    ],
})
export class DynamicAutocompleteComponent implements OnInit, AfterViewInit, ControlValueAccessor {

  @ViewChild('autocomplete')
  public autocomplete: AutoCompleteComponent | null = null; // Reference to the Syncfusion AutoComplete component

  @ViewChild('autocomplete', { read: ElementRef, static: false })
  public autocompleteElement: ElementRef | null = null;

  @Input({required: true})
  public getDataFunction!: (searchText: string) => Observable<string[] | IDynamicMultiSelectItem[] | IDynamicAutocompleteItem[]>;

  @Input()
  public placeholder = 'i18n.shared.search';

  @Input()
  public enabled = true;

  @Input()
  public set isFormComponent(value: boolean) {
    if (this._isFormComponent === value) {
      return;
    }
    this._isFormComponent = value;
    this.updateCssClass();
  }

  @Input()
  public set invalid(value: boolean) {
    if (this._invalid === value) {
      return;
    }
    this._invalid = value;
    this.updateCssClass();
  }

  @Input()
  public allowObjectBinding: boolean = false;

  @Input()
  public initialItem: IDynamicAutocompleteItem | null = null;

  public fields = { text: 'name', value: 'id' };
  public get outputValue() {
    return this._outputValue;
  }

  public set outputValue(value: any) {
    if (this._outputValue !== value) {
      this._outputValue = value;
      this._onChange(this._outputValue);
    }
  }

  public data: DataManager | null = null;
  public isFirst = false;
  public cssClass: string | null = null;
  private _invalid = false;
  private _isFormComponent = false;

  private _outputValue: any;
  private _onChange: (value: any) => void = () => {};
  private _onTouched: () => void = () => {};
  private _pendingInitialItem = false;

  constructor() {
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    if (this._outputValue !== value) {
      this._outputValue = value;
      if (this._outputValue != null && this.initialItem && this.initialItem.id == this._outputValue) {
        this._pendingInitialItem = true;
        this.applyInitialItem();
      } else if (this._outputValue) {
        this.autocomplete?.refresh();
      }
    }
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.enabled = !isDisabled;
  }

  public onSelect(data: any) {
    const value = data.itemData?.id ?? data.itemData ?? null;
    if (this._outputValue != value) {
      this._outputValue = value;
      this._onChange(this._outputValue);
    }
  }

  public onChange(args: any) {
    let value = args.itemData?.id ?? args.itemData ?? null;
    if (isNaN(value)) {
      value = null;
    }
    if (value != this._outputValue) {
      this._outputValue = value;
      this._onChange(this._outputValue);
    }
  }

  ngOnInit(): void {
    const getDataFn = this.getDataFunctionWrapped.bind(this);
    const adaptor = this.initialItem
      ? new DynamicAutocompleteInitialItemAdaptor(getDataFn, this.initialItem)
      : new DynamicAutocompleteCustomAdaptor(getDataFn);
    this.data = new DataManager({ adaptor });

    if (this.isFormComponent) {
      this.cssClass = 'form-autocomplete';
    }
  }

  ngAfterViewInit() {
    this.applyInitialItem();

    this.autocomplete?.element.addEventListener('input', e => {
      if (!this.isFirst) {
        this.isFirst = true;
        this.autocomplete?.element.parentElement
          ?.querySelector('.e-clear-icon')
          ?.addEventListener('mousedown', args => {
            this._outputValue = null;
            this._onChange(null);
          });
      }
    });
  }

  public refresh() {
    this.autocomplete?.refresh();
  }

  public onBlur() {
    this._onTouched();
    if (!this._outputValue && this.autocomplete?.value) {
      setTimeout(() => {
        if (this.autocomplete) {
          this.autocomplete.value = this._outputValue;
        }
      }, 0);
    }
  }

  public focus(): void {
    const inputElement = this.autocompleteElement?.nativeElement.querySelector('input');
    if (inputElement) {
      inputElement.focus();
    }
  }

  private getDataFunctionWrapped(searchText: string): Observable<string[] | IDynamicMultiSelectItem[] | IDynamicAutocompleteItem[]> {
    if (!this.getDataFunction) {
      return of([]);
    }
    return this.getDataFunction(searchText);
  }

  private updateCssClass() {
    this.cssClass = '';
    if (this._isFormComponent) {
      this.cssClass = 'form-autocomplete';
    }
    if (this._invalid) {
      this.cssClass += ' e-error';
    }
  }

  private applyInitialItem(): void {
    if (!this._pendingInitialItem || !this.autocomplete || !this.initialItem) {
      return;
    }
    this._pendingInitialItem = false;
    setTimeout(() => {
      if (this.autocomplete) {
        this.autocomplete.text = this.initialItem?.name as string;
      }
    });
  }
}
